import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POSITIONS = [
  "Quarterback",
  "Running Back",
  "Wide Receiver",
  "Tight End",
  "Offensive Line",
  "Defensive Line",
  "Linebacker",
  "Defensive Back",
  "Multiple / Not Sure Yet",
];

export default function Join() {
  const [form, setForm] = useState({
    parent_name: "",
    parent_email: "",
    parent_phone: "",
    player_name: "",
    player_position: "Multiple / Not Sure Yet",
    interest: "Interest meeting",
    notes: "",
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/join`, form);
      toast.success("You're in! The Ducks will reach out with meeting and tryout details.");
      setForm({
        parent_name: "",
        parent_email: "",
        parent_phone: "",
        player_name: "",
        player_position: "Multiple / Not Sure Yet",
        interest: "Interest meeting",
        notes: "",
      });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-[#0A0A0A] border border-[#333333] text-white px-4 py-3 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#FEE123] focus:border-transparent placeholder:text-gray-500";

  return (
    <>
      <PageHeader
        eyebrow="Join The Flock"
        title="Get Your Athlete In The Game"
        description="Register for an interest meeting or the February 2027 tryouts. Meet the directors, hear the vision, and claim your athlete's spot for the inaugural season."
        testid="join-header"
      />
      <section className="pt-6 pb-14 sm:pb-20 bg-[#0A0A0A]" data-testid="join-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            <Reveal className="lg:col-span-2">
              <h2 className="font-display text-4xl sm:text-5xl uppercase leading-none text-white">
                What Happens Next
              </h2>
              <ul className="mt-8 space-y-4 text-sm text-gray-300" data-testid="join-points">
                <li className="flex items-center gap-3"><span className="h-1.5 w-1.5 bg-[#FEE123] shrink-0" /> We confirm your spot and send meeting details</li>
                <li className="flex items-center gap-3"><span className="h-1.5 w-1.5 bg-[#FEE123] shrink-0" /> Meet the directors and coaching staff in person</li>
                <li className="flex items-center gap-3"><span className="h-1.5 w-1.5 bg-[#FEE123] shrink-0" /> Hear the 2027 season plan and ask anything</li>
                <li className="flex items-center gap-3"><span className="h-1.5 w-1.5 bg-[#FEE123] shrink-0" /> Tryouts begin February 2027</li>
              </ul>
              <p className="mt-8 text-xs uppercase tracking-widest text-gray-500" data-testid="join-nonprofit-note">
                Louisiana Ducks is a non-profit youth sports organization. Sponsorship opportunities are available for qualifying athletes.
              </p>
            </Reveal>

            <Reveal delay={0.15} className="lg:col-span-3">
              <form onSubmit={handleSubmit} className="bg-[#111111] border border-[#333333] p-7 sm:p-9 space-y-7" data-testid="join-form">
                <div>
                  <h3 className="text-sm font-bold uppercase tracking-[0.25em] text-[#FEE123] border-b border-[#333333] pb-3">
                    Parent / Guardian Info
                  </h3>
                  <div className="mt-5 space-y-4">
                    <input required placeholder="Parent/guardian name" value={form.parent_name} onChange={(e) => setForm({ ...form, parent_name: e.target.value })} className={inputCls} data-testid="join-parent-name" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <input required type="email" placeholder="Email address" value={form.parent_email} onChange={(e) => setForm({ ...form, parent_email: e.target.value })} className={inputCls} data-testid="join-parent-email" />
                      <input placeholder="Phone (optional)" value={form.parent_phone} onChange={(e) => setForm({ ...form, parent_phone: e.target.value })} className={inputCls} data-testid="join-parent-phone" />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-bold uppercase tracking-[0.25em] text-[#FEE123] border-b border-[#333333] pb-3">
                    Player Info
                  </h3>
                  <div className="mt-5 space-y-4">
                    <input required placeholder="Player name" value={form.player_name} onChange={(e) => setForm({ ...form, player_name: e.target.value })} className={inputCls} data-testid="join-player-name" />
                    <select value={form.player_position} onChange={(e) => setForm({ ...form, player_position: e.target.value })} className={inputCls} data-testid="join-player-position">
                      {POSITIONS.map((p) => (
                        <option key={p}>{p}</option>
                      ))}
                    </select>
                    <select value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })} className={inputCls} data-testid="join-interest">
                      <option>Interest meeting</option>
                      <option>2027 Tryouts (February)</option>
                    </select>
                    <textarea placeholder="Anything we should know? (age, experience, questions)" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} data-testid="join-notes" />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={sending}
                  data-testid="join-submit-button"
                  className="w-full bg-[#FEE123] text-black text-lg font-extrabold uppercase tracking-widest px-8 py-4 rounded-sm transition-transform duration-200 hover:scale-[1.02] hover:bg-[#FFEA61] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {sending ? "Sending..." : "Register My Athlete"}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
